import type { Theme, SxProps } from '@mui/material/styles';
import type { UnitStatReportLossSummaryViewModel } from '@/api/types/UnitStatReportLossSummaryViewModel';

import {
  Box,
  Chip,
  Table,
  Tooltip,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  Typography,
  TableContainer,
} from '@mui/material';

import { EmptyContent } from 'src/components/empty-content';

import { StyledTooltipLabel } from 'src/sections/employer-data/shared/styles/sharedStyles';
import { formatDate, formatDollarAmount } from 'src/sections/employer-data/shared/utils/policyUtils';

type UnitStatLossesTableSectionProps = {
  lossesSummary: UnitStatReportLossSummaryViewModel[];
};

const getClaimStatus = (
  claimStatusDescription: string | null | undefined
): { label: string; color: 'info' | 'default'; isGray?: boolean } | null => {
  if (!claimStatusDescription) return null;

  const status = claimStatusDescription.toLowerCase();
  if (status.includes('open')) {
    return { label: 'Open', color: 'info' };
  }
  if (status.includes('closed')) {
    return { label: 'Closed', color: 'default', isGray: true };
  }

  return { label: claimStatusDescription, color: 'default', isGray: true };
};

const getClaimStatusChipSx = (
  claimStatus: { color: 'info' | 'default'; isGray?: boolean } | null
): SxProps<Theme> | undefined => {
  if (!claimStatus) {
    return undefined;
  }

  if (claimStatus.color === 'info') {
    return (theme) => ({
      backgroundColor: theme.palette.info.main + '29',
      color: theme.palette.info.dark,
    });
  }

  if (claimStatus.isGray) {
    return (theme) => ({
      backgroundColor: theme.palette.grey[300],
      color: theme.palette.grey[800],
    });
  }

  return undefined;
};

export default function UnitStatLossesTableSection({ lossesSummary }: Readonly<UnitStatLossesTableSectionProps>) {
  const sortedLosses = [...lossesSummary].sort(
    (a: UnitStatReportLossSummaryViewModel, b: UnitStatReportLossSummaryViewModel) => {
      const dateA = a.accidentDate ? new Date(a.accidentDate).getTime() : 0;
      const dateB = b.accidentDate ? new Date(b.accidentDate).getTime() : 0;
      return dateB - dateA;
    }
  );

  return (
    <Box
      sx={{
        mt: 2,
        border: '0.0625em solid var(--palette-divider)',
        borderRadius: 2,
      }}
    >
      <Box sx={{ p: 2, borderBottom: '0.0625em solid var(--palette-divider)' }}>
        <Typography
          variant="h6"
          sx={{ color: 'text.primary' }}
          data-testid="losses-summary-title"
        >
          Losses
        </Typography>
      </Box>
      {sortedLosses.length === 0 ? (
        <EmptyContent
          filled
          title="No data to display"
          description="This table will be updated if the policy has any Unit Stat Report Losses to report."
          sx={{ py: 10 }}
        />
      ) : (
        <TableContainer
          sx={(theme) => ({
            maxHeight: '15em',
            '&::-webkit-scrollbar': {
              width: '0.5em',
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: theme.palette.action.hover,
              borderRadius: '0.25em',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: theme.palette.grey[400],
              borderRadius: '0.25em',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: theme.palette.grey[600],
            },
            scrollbarWidth: 'thin',
            scrollbarColor: `${theme.palette.grey[400]} ${theme.palette.action.hover}`,
          })}
        >
          <Table size="small" data-testid="unit-stat-losses-summary-table" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Claim number</TableCell>
                <TableCell>Last updated</TableCell>
                <TableCell>Accident date</TableCell>
                <TableCell>Indemnity</TableCell>
                <TableCell>Medical</TableCell>
                <TableCell>Class code</TableCell>
                <TableCell>Injury type</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLosses.map((loss: UnitStatReportLossSummaryViewModel, index: number) => {
                const claimStatus = getClaimStatus(loss.claimStatusDescription);
                const claimStatusChipSx = getClaimStatusChipSx(claimStatus);

                return (
                  <TableRow key={loss.claimNumber} data-testid={`loss-summary-row-${index}`}>
                    <TableCell data-testid={`loss-summary-row-${index}-claim-number`}>
                      {loss.claimNumber}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-last-updated`}>
                      {loss.lastUpdated}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-accident-date`}>
                      {loss.accidentDate ? formatDate(new Date(loss.accidentDate)) : ''}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-incurred-indemnity`}>
                      {formatDollarAmount(loss.incurredIndemnity)}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-incurred-medical`}>
                      {formatDollarAmount(loss.incurredMedical)}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-class-code`}>
                      {loss.classCode && loss.classDescription ? (
                        <Tooltip
                          arrow
                          title={loss.classDescription}
                          placement="top"
                          data-testid={`loss-summary-row-${index}-class-code-tooltip-content`}
                        >
                          <StyledTooltipLabel
                            data-testid={`loss-summary-row-${index}-class-code-tooltip`}
                          >
                            <Typography component="span" sx={{ typography: 'body2' }}>
                              {loss.classCode}
                            </Typography>
                          </StyledTooltipLabel>
                        </Tooltip>
                      ) : (
                        loss.classCode
                      )}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-injury-type`}>
                      {loss.injuryTypeDescription}
                    </TableCell>
                    <TableCell data-testid={`loss-summary-row-${index}-claim-status`}>
                      {claimStatus ? (
                        <Chip
                          label={claimStatus.label}
                          color={claimStatus.color}
                          size="small"
                          data-testid={`loss-summary-row-${index}-claim-status-chip`}
                          sx={claimStatusChipSx}
                        />
                      ) : (
                        loss.claimStatusDescription
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
